import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import api, { apiError, mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useI18n, LanguageToggle } from "@/context/I18n";
import { loadLikes } from "@/lib/likes";
import PlayerBar from "@/components/music/PlayerBar";
import InstallPrompt from "@/components/InstallPrompt";
import BackgroundAudioNotice from "@/components/BackgroundAudioNotice";
import MobileNav from "@/components/music/MobileNav";
import AccountMenu from "@/components/music/AccountMenu";
import GlobalSearch from "@/components/music/GlobalSearch";
import { Compass, Disc3, Heart, Home, Library, ListMusic, LogOut, Mic2, Pencil, Plus, Search, Settings as SettingsIcon, Sparkles, Trash2 } from "lucide-react";

export default function AppShell() {
  const [playlists, setPlaylists] = useState([]);
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();

  const loadPlaylists = () => api.get("/music/playlists").then((r) => setPlaylists(r.data.playlists)).catch(() => {});
  useEffect(() => {
    loadPlaylists();
    loadLikes();
    const h = () => loadPlaylists();
    window.addEventListener("playlists-changed", h);
    return () => window.removeEventListener("playlists-changed", h);
  }, []);

  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [composing, setComposing] = useState(false);
  const [draftName, setDraftName] = useState("");

  const renamePlaylist = async (id) => {
    const name = editingName.trim();
    setEditingId(null);
    if (!name) return;
    try {
      await api.patch(`/music/playlists/${id}`, { name });
      loadPlaylists();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const deletePlaylist = async (pl) => {
    if (!window.confirm(`${t("delete")} "${pl.name}"?`)) return;
    try {
      await api.delete(`/music/playlists/${pl.id}`);
      toast.success(t("playlist_deleted"));
      loadPlaylists();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const createPlaylist = async (rawName) => {
    const name = (rawName || "").trim();
    if (!name) return;
    try {
      const { data } = await api.post("/music/playlists", { name });
      toast.success(t("playlist_created"));
      setComposing(false);
      setDraftName("");
      loadPlaylists();
      navigate(`/app/playlist/${data.id}`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const doLogout = async () => { await logout(); navigate("/login"); };

  const navCls = ({ isActive }) =>
    `flex items-center gap-3.5 px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
      isActive ? "text-white bg-white/10" : "text-zinc-400 hover:text-white"}`;

  return (
    <div className="h-screen bg-[#050505] text-white flex flex-col overflow-hidden">
      <div className="flex-1 flex gap-2 p-2 pb-[150px] md:pb-[92px] overflow-hidden">
        <aside className="hidden md:flex w-64 shrink-0 flex-col gap-2">
          <div className="bg-[#0A0A0A] border border-white/5 rounded-xl p-3">
            <div className="flex items-center gap-2.5 px-2 py-2 mb-1">
              <img src="/icon.jpg" alt="SONLYO" className="w-8 h-8 rounded-lg" />
              <span className="font-display text-xl font-black tracking-tighter">SONLYO</span>
            </div>
            <NavLink to="/app" end className={navCls} data-testid="nav-home"><Home className="w-5 h-5" /> {t("nav_home")}</NavLink>
            <NavLink to="/app/search" className={navCls} data-testid="nav-search"><Search className="w-5 h-5" /> {t("nav_search")}</NavLink>
            <NavLink to="/app/browse" className={navCls} data-testid="nav-browse"><Compass className="w-5 h-5" /> {t("nav_browse")}</NavLink>
            <NavLink to="/app/for-you" className={navCls} data-testid="nav-foryou"><Sparkles className="w-5 h-5" /> {t("made_for_you")}</NavLink>
            <NavLink to="/app/artists" className={navCls} data-testid="nav-artists"><Mic2 className="w-5 h-5" /> {t("nav_artists")}</NavLink>
            <NavLink to="/app/albums" className={navCls} data-testid="nav-albums"><Disc3 className="w-5 h-5" /> {t("nav_albums")}</NavLink>
            <NavLink to="/app/collections" className={navCls} data-testid="nav-collections"><ListMusic className="w-5 h-5" /> {t("collections")}</NavLink>
            <NavLink to="/app/liked" className={navCls} data-testid="nav-liked"><Heart className="w-5 h-5" /> {t("nav_liked")}</NavLink>
          </div>

          <div className="bg-[#0A0A0A] border border-white/5 rounded-xl p-3 flex-1 overflow-y-auto">
            <div className="flex items-center justify-between px-2 py-1 mb-2">
              <NavLink to="/app/library" className="flex items-center gap-2.5 text-sm font-semibold text-zinc-400 hover:text-white" data-testid="nav-library">
                <Library className="w-5 h-5" /> {t("nav_library")}
              </NavLink>
              <button
                onClick={() => setComposing((v) => !v)}
                data-testid="create-playlist-btn"
                title={t("create_playlist")}
                className={`p-1 rounded-md transition-colors ${composing ? "text-[#FF2A4D] bg-white/10" : "text-zinc-400 hover:text-white hover:bg-white/10"}`}
              >
                <Plus className={`w-4 h-4 transition-transform ${composing ? "rotate-45" : ""}`} />
              </button>
            </div>

            {composing && (
              <form
                data-testid="new-playlist-form"
                onSubmit={(e) => { e.preventDefault(); createPlaylist(draftName); }}
                className="px-2 pb-2"
              >
                <input
                  autoFocus
                  data-testid="new-playlist-input"
                  value={draftName}
                  onChange={(e) => setDraftName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Escape") { setComposing(false); setDraftName(""); } }}
                  onBlur={() => { if (!draftName.trim()) setComposing(false); }}
                  placeholder={t("playlist_name_ph")}
                  maxLength={80}
                  className="w-full bg-[#050505] border border-white/10 rounded-lg px-3 py-2 text-sm placeholder-zinc-600 focus:outline-none focus:border-[#FF2A4D]"
                />
                <div className="flex gap-2 mt-2">
                  <button type="submit" disabled={!draftName.trim()}
                    className="flex-1 bg-[#FF2A4D] hover:bg-[#E61C3D] disabled:opacity-50 rounded-lg py-1.5 text-xs font-semibold">
                    {t("create")}
                  </button>
                  <button type="button" onClick={() => { setComposing(false); setDraftName(""); }}
                    className="px-3 rounded-lg text-xs font-semibold text-zinc-400 hover:text-white hover:bg-white/10">
                    {t("cancel")}
                  </button>
                </div>
              </form>
            )}
            <div className="space-y-0.5">
              {playlists.map((pl) => (
              <div key={pl.uuid || pl.id} className="group relative flex items-center">
                <NavLink
                  to={`/app/playlist/${pl.id}`}
                  className={({ isActive }) =>
                    `flex-1 min-w-0 truncate px-2 py-1.5 rounded-md text-sm transition-colors ${
                      isActive ? "text-white bg-white/10" : "text-zinc-400 hover:text-white hover:bg-white/5"
                    }`
                  }
                  data-testid={`playlist-link-${pl.id}`}
                >
                  {editingId === pl.id ? null : pl.name}
                </NavLink>

                {editingId === pl.id ? (
                  <input
                    autoFocus
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") renamePlaylist(pl.id);
                      if (e.key === "Escape") setEditingId(null);
                    }}
                    onBlur={() => renamePlaylist(pl.id)}
                    data-testid={`playlist-rename-input-${pl.id}`}
                    className="absolute inset-0 bg-[#050505] border border-[#FF2A4D] rounded-md px-2 text-sm focus:outline-none"
                  />
                ) : (
                  <span className="absolute right-1 hidden group-hover:flex items-center gap-0.5 bg-[#0A0A0A] pl-1">
                    <button
                      onClick={() => { setEditingId(pl.id); setEditingName(pl.name); }}
                      title={t("rename")}
                      data-testid={`playlist-rename-${pl.id}`}
                      className="p-1 rounded text-zinc-500 hover:text-white hover:bg-white/10"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => deletePlaylist(pl)}
                      title={t("delete")}
                      data-testid={`playlist-delete-${pl.id}`}
                      className="p-1 rounded text-zinc-500 hover:text-red-400 hover:bg-white/10"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </span>
                )}
              </div>
            ))}
              {playlists.length === 0 && <p className="text-xs text-zinc-500 px-2 py-3">{t("first_playlist")}</p>}
            </div>
          </div>
        </aside>

        <main className="flex-1 bg-gradient-to-b from-[#141414] to-[#0A0A0A] rounded-xl overflow-y-auto">
          <div className="sticky top-0 z-30 flex items-center justify-between gap-2 md:gap-4 px-3 md:px-6 py-3 bg-[#141414]/80 backdrop-blur-xl border-b border-white/5">
            <NavLink to="/app" className="md:hidden flex items-center gap-2 shrink-0" aria-label="SONLYO">
              <img src="/icon-192.png" alt="" className="w-7 h-7 rounded-lg" />
            </NavLink>
            <div className="flex-1 min-w-0">
              <GlobalSearch />
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              {/* Wide screens keep the actions in reach; a phone collapses them
                  behind the avatar, where they always fit. */}
              <div className="hidden md:flex items-center gap-1.5">
                <LanguageToggle />
                <NavLink to="/app/settings" data-testid="nav-settings" className="p-2 rounded-full text-zinc-400 hover:text-white hover:bg-white/10" title={t("nav_settings")}>
                  <SettingsIcon className="w-5 h-5" />
                </NavLink>
                <NavLink to="/app/settings" data-testid="profile-btn" title={t("nav_profile")}
                  className="w-8 h-8 rounded-full bg-[#FF2A4D] hover:brightness-110 flex items-center justify-center text-xs font-bold overflow-hidden transition">
                  {user?.avatar_url
                    ? <img src={mediaUrl(user.avatar_url)} alt="" className="w-full h-full object-cover" />
                    : (user?.name || "U")[0].toUpperCase()}
                </NavLink>
                <button onClick={doLogout} data-testid="logout-btn" title={t("logout")}
                  className="p-2 rounded-full text-zinc-400 hover:text-white hover:bg-white/10 transition-colors">
                  <LogOut className="w-5 h-5" />
                </button>
              </div>
              <div className="md:hidden">
                <AccountMenu user={user} onLogout={doLogout} />
              </div>
            </div>
          </div>
          <Outlet />
        </main>
      </div>
      <PlayerBar />
      <MobileNav />
      <InstallPrompt />
      <BackgroundAudioNotice />
    </div>
  );
}
